import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Field, SectionTitle, Spinner, StatusPill } from "@/components/common";
import { useLoad, useTrip } from "@/context/TripContext";
import { api, errMsg, fmtDate, money } from "@/lib/api";
import CardPay, { useCardReturn } from "@/components/CardPay";

export function Breakdown({ m, cur }) {
  if (!m) return <p className="text-sm text-slate-500">No costs yet.</p>;
  return (
    <div data-testid="cost-breakdown">
      <ul className="divide-y divide-slate-100">
        {m.lines.map((l, i) => (
          <li key={i} className="flex items-start justify-between gap-3 py-2 text-sm">
            <div><p className="font-medium">{l.label}</p><p className="text-xs text-slate-500">{l.note}</p></div>
            <span className={`font-mono ${l.amount_minor < 0 ? "text-teal-700" : ""}`}>{money(l.amount_minor, cur)}</span>
          </li>
        ))}
        {m.lines.length === 0 && <li className="py-2 text-sm text-slate-500">No charges assigned yet.</li>}
      </ul>
      <div className="mt-3 space-y-1 rounded-xl bg-slate-50 p-3 font-mono text-sm">
        <div className="flex justify-between font-semibold"><span>Total</span><span data-testid="breakdown-total">{money(m.total_minor, cur)}</span></div>
        <div className="flex justify-between text-teal-700"><span>Recorded as paid</span><span data-testid="breakdown-paid">{money(m.paid_minor, cur)}</span></div>
        <div className="flex justify-between font-semibold text-orange-600"><span>Remaining</span><span data-testid="breakdown-balance">{money(m.balance_minor, cur)}</span></div>
        {m.credit_minor > 0 && <div className="flex justify-between text-teal-700"><span>Credit (overpaid)</span><span>{money(m.credit_minor, cur)}</span></div>}
        {m.overdue_minor > 0 && <div className="flex justify-between text-rose-600"><span>Overdue</span><span>{money(m.overdue_minor, cur)}</span></div>}
      </div>
      {m.installments.length > 0 && (
        <div className="mt-4"><p className="mb-2 text-sm font-semibold">Payment schedule</p>
          {m.installments.map((i) => (
            <div key={i.installment_id} className="flex items-center justify-between py-1.5 text-sm">
              <span>{i.label}{i.is_deposit && " (deposit)"} · {fmtDate(i.due_date)}</span>
              <span className="flex items-center gap-2 font-mono">{money(i.amount_minor, cur)} <StatusPill status={i.status} /></span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Rsvp() {
  const { tid, me, reload } = useTrip();
  const set = async (status) => { try { await api.patch(`/trips/${tid}/me`, { status }); toast.success("RSVP updated"); reload(); } catch (e) { toast.error(errMsg(e)); } };
  if (me.role === "organizer") return null;
  return (
    <div className="card p-5"><h3 className="font-bold">Are you coming?</h3>
      <div className="mt-3 flex flex-wrap gap-2">
        {[["accepted", "I'm in"], ["maybe", "Maybe"], ["declined", "Can't make it"]].map(([s, l]) => (
          <Button key={s} variant={me.status === s ? "default" : "outline"} className="rounded-full" onClick={() => set(s)} data-testid={`rsvp-${s}`}>{l}</Button>
        ))}
      </div>
    </div>
  );
}

const PREFS = [["dietary", "Dietary needs"], ["accessibility", "Accessibility needs"], ["arrival_needs", "Arrival / transport needs"], ["interests", "Activities I'd love"], ["budget_comfort", "Budget comfort (e.g. $1–2k)"], ["roommate_notes", "Roommate notes"], ["notes", "Anything else for the organizer"]];

function Preferences({ members }) {
  const { tid, me, reload } = useTrip();
  const [p, setP] = useState(me.preferences || {});
  const save = async () => { try { await api.patch(`/trips/${tid}/me`, { preferences: p }); toast.success("Preferences saved"); reload(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="card space-y-3 p-5">
      <h3 className="font-bold">My preferences</h3><p className="text-xs text-slate-500">Private — only you and the organizers can see these. Share only what you're comfortable with.</p>
      <Field label="Roommate request" htmlFor="rmreq">
        <select id="rmreq" className="nselect" value={p.roommate_request || ""} onChange={(e) => setP({ ...p, roommate_request: e.target.value })} data-testid="pref-roommate-select">
          <option value="">No preference</option>{members.filter((m) => m.member_id !== me.member_id).map((m) => <option key={m.member_id} value={m.member_id}>{m.name}</option>)}
        </select>
      </Field>
      {PREFS.map(([k, l]) => <Field key={k} label={l} htmlFor={`p-${k}`}><Input id={`p-${k}`} value={p[k] || ""} onChange={(e) => setP({ ...p, [k]: e.target.value })} data-testid={`pref-${k}-input`} /></Field>)}
      <Button className="rounded-full" onClick={save} data-testid="pref-save-button">Save preferences</Button>
    </div>
  );
}

function OptionalActs({ onChange }) {
  const { tid, cur } = useTrip();
  const [it, reload] = useLoad(`/trips/${tid}/itinerary`);
  const opts = (it?.items || []).filter((i) => i.optional && i.plan_id === it.current_plan_id);
  const toggle = async (i, v) => { try { await api.post(`/trips/${tid}/itinerary/${i.item_id}/opt`, { opt_in: v }); toast.success(v ? "You're in — your total was updated" : "Removed — your total was updated"); reload(); onChange(); } catch (e) { toast.error(errMsg(e)); } };
  if (!opts.length) return null;
  return (
    <div className="card p-5"><h3 className="font-bold">Optional activities</h3><p className="text-xs text-slate-500">You're only charged for what you opt into.</p>
      {opts.map((i) => { const on = i.opt_ins.includes(it.my_member_id); return (
        <div key={i.item_id} className="mt-3 flex items-center justify-between gap-3 text-sm">
          <div><p className="font-medium">{i.title}</p><p className="text-xs text-slate-500">{fmtDate(i.date)} · {money(i.price_minor, cur)} pp{i.deadline ? ` · decide by ${fmtDate(i.deadline)}` : ""}</p></div>
          <Button size="sm" variant={on ? "default" : "outline"} className="rounded-full" onClick={() => toggle(i, !on)} data-testid={`myopt-${i.item_id}`}>{on ? "Going ✓" : "Join"}</Button>
        </div>); })}
    </div>
  );
}

export default function MyTrip() {
  const { tid, cur } = useTrip();
  const [f, reloadF] = useLoad(`/trips/${tid}/finance`);
  const [members] = useLoad(`/trips/${tid}/members`);
  const [card] = useLoad(`/trips/${tid}/card-payments`);
  useCardReturn(reloadF);
  if (!f || !members) return <Spinner />;
  const pays = (f.payments || []).filter((p) => p.member_id === f.me?.member_id);
  return (
    <div className="space-y-6">
      <SectionTitle title="My trip" sub="Your personal costs, schedule and details." />
      <div className="grid gap-6 lg:grid-cols-2">
        <div className="space-y-6">
          <Rsvp />
          <div className="card p-5"><h3 className="mb-3 font-bold">My cost breakdown</h3><Breakdown m={f.me} cur={cur} />
            {f.me?.next_due && <p className="mt-3 rounded-xl bg-orange-50 p-3 text-sm text-orange-800" data-testid="my-next-due">Next installment: {money(f.me.next_due.amount_minor, cur)} due {fmtDate(f.me.next_due.due_date)}</p>}</div>
          <div className="card p-5"><h3 className="font-bold">Recorded payments</h3><p className="text-xs text-slate-500">{f.verification_note}</p>
            {pays.length === 0 ? <p className="mt-2 text-sm text-slate-500">None recorded yet.</p> : pays.map((p) => (
              <div key={p.payment_id} className={`mt-2 flex justify-between text-sm ${p.voided ? "line-through opacity-50" : ""}`}><span>{fmtDate(p.paid_on)} · {p.method} {p.kind === "refund" && "(refund)"}</span><span className="font-mono">{money(p.amount_minor, cur)} {p.verification === "stripe" && <span className="pill bg-teal-100 text-teal-700">card · verified by Stripe</span>}</span></div>))}
          </div>
          {card?.enabled && <CardPay balance={f.me?.balance_minor} cur={cur} feeBps={card.fee_bps} payPath={`/trips/${tid}/pay`} />}
        </div>
        <div className="space-y-6"><OptionalActs onChange={reloadF} /><Preferences members={members} /></div>
      </div>
    </div>
  );
}
